<script setup>
import { ref, computed, watch } from 'vue'
import FormCreator from '../UI/Form/FormCreator.vue'
import LazyContent from '../UI/Loading/LazyContent.vue'
import FieldCreator from '../UI/Form/FieldCreator.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import SignatureBlock from '../Contract/SignatureBlock.vue'
import { getNumber, getRoundFloat } from '../../Utils/numeric'
import { useI18n } from 'vue-i18n'
import useSearchParams from '../../Composables/useSearchParams'

const { t } = useI18n()
const { searchParams } = useSearchParams()

const props = defineProps({
  financeId: { type: Number, required: true },
  surrenderId: { type: Number, required: false, default: null },
  entityId: { type: Number, required: false, default: null },
  method: { type: String, required: false, default: '' },
  submitDisabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const emit = defineEmits([
  'surrenders-sent',
  'update:surrenderSigner',
  'update-surrenderSigner',
  'add-surrenderSigner',
  'update:surrender',
  'add-surrender',
  'update:surrenderApproverSigners',
  'add-surrenderApproverSigners',
])

const def = ref({})
const surrender = ref({})
const sending = ref(false)
const loading = ref(false)
const formData = ref({})
const build = ref(false)
const page = ref(searchParams.get('p'))
const myformsurrender = ref(null)

const params = ref({
  financeId: props.financeId,
  id: props.surrenderId,
  entityId: props.entityId,
})

const amoutLabel = computed(() => {
  return page.value === 'action' ? 'surrender.fields.amount.labelReal' : 'surrender.fields.amount.label'
})

const percentLabel = computed(() => {
  return page.value === 'action' ? 'surrender.fields.percent.labelReal' : 'surrender.fields.percent.label'
})

const showSignersBlock = computed(() => {
  return !!props.entityId || surrender.value?.entity?.id
})

const updateData = (newData) => {
  formData.value = newData
}

const globalBuild = ({ loading: loadingVal, build: buildVal, def: defVal, object }) => {
  build.value = buildVal
  loading.value = loadingVal
  def.value = defVal
  surrender.value = object
}

const objectReturn = ({ sending: sendingVal, object }) => {
  sending.value = sendingVal

  if (object) {
    emit('surrenders-sent', { json: object, method: props.method })
  }
}

const getAmount = (val) => {
  if (surrender.value.equipmentAmount) {
    const amount = getNumber(val)
    const percent = getRoundFloat((amount / surrender.value.equipmentAmount) * 100, 1, 100)
    surrender.value.percent = percent
  }
}

const getPercent = (val) => {
  if (surrender.value.equipmentAmount) {
    const percent = getNumber(val)
    const amount = getRoundFloat(surrender.value.equipmentAmount, percent / 100, 100)
    surrender.value.amount = amount
  }
}

const sendSurrender = () => {
  myformsurrender.value.methods(props.surrenderId ? 'patch' : undefined)
}

const handleSurrenderSelection = (val) => {
  params.value.entityId = val

  myformsurrender.value.get()

  emit('update:surrender')
}

const handleSignerChange = () => {
  if (props.disabled) {
    myformsurrender.value.get()
  }
}

const onSendingChanged = (sendingVal) => {
  sending.value = sendingVal
}
</script>
<template>
  <div>
    <FormCreator
      :id="surrenderId"
      ref="myformsurrender"
      endpoint="v2/surrenders"
      :method="method"
      :params="params"
      :disabled="disabled"
      class-to-add="d-none"
      @global-build="globalBuild"
      @object-return="objectReturn"
      @is-sending="onSendingChanged"
      @update-data="updateData"
    />

    <LazyContent :loading="loading">
      <FieldCreator
        v-if="build"
        :form-data="formData"
        name="entity"
        form-ref="myformsurrender"
        domain="surrender"
        label-top
        @get-select-change="handleSurrenderSelection"
      />
      <div class="row">
        <div class="col-md-5">
          <FieldCreator
            v-if="build"
            :form-data="formData"
            name="amount"
            form-ref="myformsurrender"
            domain="surrender"
            label-top
            :label="amoutLabel"
            @get-input-change="getAmount"
          />
        </div>
        <div class="col-md-2 font-weight-bold d-flex justify-content-center align-items-center">ou</div>
        <div class="col-md-5">
          <FieldCreator
            v-if="build"
            :form-data="formData"
            name="percent"
            form-ref="myformsurrender"
            domain="surrender"
            label-top
            :label="percentLabel"
            @get-input-change="getPercent"
          />
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <FieldCreator
            v-if="build"
            :form-data="formData"
            name="amountReal"
            form-ref="myformsurrender"
            domain="surrender"
            label-top
          />
        </div>
        <div class="col-md-6">
          <FieldCreator
            v-if="build"
            :form-data="formData"
            name="purchaseAmountReal"
            form-ref="myformsurrender"
            domain="surrender"
            label-top
          />
        </div>
      </div>
      <div v-if="showSignersBlock" class="col-md-6 flex-fill pl-0">
        <h5>{{ t('contract.information.signers') }}</h5>
        <SignatureBlock
          v-if="build"
          :object="surrender"
          :form-data="formData"
          form-ref="myformsurrender"
          :form-instance="$refs.myformsurrender"
          domain="contract"
          classes="mb-4"
          @add-surrender-signer="emit('add-surrenderSigner', $event)"
          @update-surrender-signer="emit('update-surrenderSigner', $event)"
          @add-surrender="emit('add-surrender', $event)"
          @update:surrender="emit('update:surrender', $event)"
          @add-surrender-approver-signers="emit('add-surrenderApproverSigners', $event)"
          @update:surrender-approver-signers="emit('update:surrenderApproverSigners', $event)"
        />
      </div>
      <slot name="messages"></slot>

      <div v-if="!disabled" class="mt-3">
        <ButtonAll
          type="validation"
          label="surrender.send"
          margin="no"
          :loading="sending"
          :disabled="submitDisabled"
          e2e-button="createSurrender"
          @click="sendSurrender"
        />

        <slot name="buttons"></slot>
      </div>
    </LazyContent>
  </div>
</template>
